import { stringifyFrontMatter, type FrontMatter } from "./front-matter";

export interface TemplateConfig {
  type: string;
  label: string;
  description: string;
  coverLayout: "standard" | "centered" | "split" | "minimal";
  headerTitlePattern: string;
  sections: TemplateSection[];
  footerType: "confidential" | "standard" | "none";
  metadataFields: string[];
  hasLetterhead: boolean;
  hasSignatureBlock: boolean;
  hasFormulas: boolean;
}

export interface TemplateSection {
  id: string;
  label: string;
  level: number;
  required: boolean;
  content:
    | "prose"
    | "bullets"
    | "formula"
    | "metadata"
    | "checkboxes"
    | "signature"
    | "letterhead";
}

function sectionPlaceholder(section: TemplateSection): string {
  const heading = "#".repeat(section.level + 1) + " " + section.label;
  let body: string;
  switch (section.content) {
    case "prose":
      body = "Write your " + section.label.toLowerCase() + " here.";
      break;
    case "bullets":
      body = "- Item 1\n- Item 2\n- Item 3";
      break;
    case "formula":
      body = "$$formula$$";
      break;
    case "metadata":
      body = "<!-- metadata section: auto-generated -->";
      break;
    case "checkboxes":
      body = "- [ ] Action item 1\n- [ ] Action item 2\n- [ ] Action item 3";
      break;
    case "signature":
      body = "_________________________\nName / Title / Date";
      break;
    case "letterhead":
      body = "[Letterhead — Organization name, address, date]";
      break;
    default:
      body = "";
  }
  return heading + "\n\n" + body;
}

export function generateTemplateMarkdown(type: string): string {
  const config = TEMPLATES[type];
  if (!config) {
    throw new Error(`Unknown template type: ${type}`);
  }

  const today = new Date().toISOString().split("T")[0];

  // Build front matter with all known fields
  const fm: Record<string, unknown> = {
    notion_page_id: "",
    notion_last_edited_time: "",
    content_hash: "",
    doc_type: type,
    family: "",
    version: "v0.1",
    status: "draft",
    title: `New ${config.label}`,
    effective_date: today,
    supersedes: "",
    template: type,
  };

  // Add template-specific metadata fields as empty defaults
  for (const field of config.metadataFields) {
    if (!(field in fm)) {
      fm[field] = "";
    }
  }

  // Build section content
  const sectionLines: string[] = [];
  for (const section of config.sections) {
    if (section.content === "metadata" && section.id === "toc") {
      // Skip auto-generated TOC sections in the markdown body
      continue;
    }
    sectionLines.push(sectionPlaceholder(section));
    sectionLines.push("");
  }

  const content = sectionLines.join("\n");

  // stringifyFrontMatter handles YAML escaping
  return stringifyFrontMatter(fm as FrontMatter, content);
}

export const TEMPLATES: Record<string, TemplateConfig> = {
  factsheet: {
    type: "factsheet",
    label: "Factsheet",
    description: "Index/product factsheet with cover, TOC, and metadata",
    coverLayout: "standard",
    headerTitlePattern: "{TICKER} FACTSHEET",
    sections: [
      {
        id: "toc",
        label: "Table of Contents",
        level: 0,
        required: true,
        content: "metadata",
      },
      {
        id: "overview",
        label: "Index Overview",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "construction",
        label: "Construction Methodology",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "performance",
        label: "Performance Statistics",
        level: 1,
        required: true,
        content: "formula",
      },
      {
        id: "governance",
        label: "Governance & Disclaimer",
        level: 1,
        required: true,
        content: "prose",
      },
    ],
    footerType: "confidential",
    metadataFields: [
      "title",
      "ticker",
      "category",
      "baseDate",
      "rebalance",
      "administrator",
    ],
    hasLetterhead: false,
    hasSignatureBlock: false,
    hasFormulas: true,
  },
  methodology: {
    type: "methodology",
    label: "Methodology",
    description: "Full methodology document with formulas and governance",
    coverLayout: "centered",
    headerTitlePattern: "{TICKER} METHODOLOGY",
    sections: [
      {
        id: "toc",
        label: "Table of Contents",
        level: 0,
        required: true,
        content: "metadata",
      },
      {
        id: "introduction",
        label: "Introduction",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "family",
        label: "Index Family Overview",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "eligibility",
        label: "Market Eligibility",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "calculation",
        label: "Index Calculation",
        level: 1,
        required: true,
        content: "formula",
      },
      {
        id: "rebalancing",
        label: "Rebalancing",
        level: 2,
        required: true,
        content: "prose",
      },
      {
        id: "actions",
        label: "Corporate Actions & Disruptions",
        level: 1,
        required: false,
        content: "bullets",
      },
      {
        id: "governance",
        label: "Index Governance",
        level: 1,
        required: true,
        content: "metadata",
      },
      {
        id: "disclaimer",
        label: "Disclaimer",
        level: 1,
        required: true,
        content: "prose",
      },
    ],
    footerType: "confidential",
    metadataFields: [
      "title",
      "ticker",
      "administrator",
      "committeeChair",
      "reviewCycle",
      "lastReviewed",
    ],
    hasLetterhead: false,
    hasSignatureBlock: false,
    hasFormulas: true,
  },
  loi: {
    type: "loi",
    label: "Letter of Intent",
    description: "Partnership LOI with letterhead and signatures",
    coverLayout: "split",
    headerTitlePattern: "",
    sections: [
      {
        id: "toc",
        label: "Table of Contents",
        level: 0,
        required: false,
        content: "metadata",
      },
      {
        id: "body",
        label: "Letter Body",
        level: 0,
        required: true,
        content: "letterhead",
      },
      {
        id: "license",
        label: "Proposed Index License",
        level: 2,
        required: true,
        content: "bullets",
      },
      {
        id: "exclusivity",
        label: "Exclusivity & Term",
        level: 2,
        required: true,
        content: "bullets",
      },
      {
        id: "signature",
        label: "Signatures",
        level: 0,
        required: true,
        content: "signature",
      },
    ],
    footerType: "confidential",
    metadataFields: ["title", "date", "addressee", "subject"],
    hasLetterhead: true,
    hasSignatureBlock: true,
    hasFormulas: false,
  },
  memo: {
    type: "memo",
    label: "Internal Memo",
    description: "Confidential memo with metadata and action items",
    coverLayout: "minimal",
    headerTitlePattern: "{SUBJECT} — Q{QUARTER} {YEAR} OUTLOOK",
    sections: [
      {
        id: "toc",
        label: "Table of Contents",
        level: 0,
        required: false,
        content: "metadata",
      },
      {
        id: "summary",
        label: "Executive Summary",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "performance",
        label: "Performance Review",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "metrics",
        label: "Volatility & Liquidity Metrics",
        level: 2,
        required: true,
        content: "formula",
      },
      {
        id: "outlook",
        label: "Outlook",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "actions",
        label: "Recommended Actions",
        level: 1,
        required: true,
        content: "checkboxes",
      },
      {
        id: "distribution",
        label: "Distribution",
        level: 2,
        required: false,
        content: "prose",
      },
    ],
    footerType: "confidential",
    metadataFields: [
      "title",
      "to",
      "from",
      "date",
      "subject",
      "classification",
    ],
    hasLetterhead: false,
    hasSignatureBlock: false,
    hasFormulas: true,
  },
  researchNote: {
    type: "researchNote",
    label: "Research Note",
    description: "Analyst research note with thesis and data",
    coverLayout: "minimal",
    headerTitlePattern: "{TITLE}",
    sections: [
      {
        id: "summary",
        label: "Summary",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "thesis",
        label: "Investment Thesis",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "data",
        label: "Supporting Data",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "risks",
        label: "Risks",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "disclaimer",
        label: "Disclaimer",
        level: 2,
        required: true,
        content: "prose",
      },
    ],
    footerType: "standard",
    metadataFields: ["title", "author", "date", "classification"],
    hasLetterhead: false,
    hasSignatureBlock: false,
    hasFormulas: false,
  },
  governanceMinutes: {
    type: "governanceMinutes",
    label: "Governance Minutes",
    description: "Committee meeting minutes and resolutions",
    coverLayout: "standard",
    headerTitlePattern: "{COMMITTEE} MINUTES",
    sections: [
      {
        id: "attendees",
        label: "Attendees",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "agenda",
        label: "Agenda",
        level: 1,
        required: true,
        content: "bullets",
      },
      {
        id: "discussion",
        label: "Discussion",
        level: 1,
        required: true,
        content: "prose",
      },
      {
        id: "resolutions",
        label: "Resolutions",
        level: 1,
        required: true,
        content: "checkboxes",
      },
      {
        id: "actions",
        label: "Action Items",
        level: 1,
        required: true,
        content: "checkboxes",
      },
    ],
    footerType: "confidential",
    metadataFields: ["title", "committee", "date", "location"],
    hasLetterhead: false,
    hasSignatureBlock: true,
    hasFormulas: false,
  },
};
